import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Sidebar from '../components/Sidebar';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Progress } from '@/components/ui/progress';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Brain, Sparkles, FileText, AlertCircle, Loader2, ChevronDown, ChevronRight, ExternalLink, Users, Archive, CheckCircle2, Eye, EyeOff, Download, Edit2, Save, Trash2, Paperclip, Key, AlertTriangle, User, Building2, Lock, ListChecks, Calendar, Upload, GraduationCap, Search, Plus } from 'lucide-react';
import api, { formatErrorMessage, settleRequests } from '../utils/api';
import { toast } from 'sonner';
import TaskAttachments from '../components/TaskAttachments';
import ClientCredentials from '../components/ClientCredentials';
import HierarchicalCredentials from '../components/HierarchicalCredentials';
import BrainLearningPanel from '../components/BrainLearningPanel';
import { ORIGEN_OPTIONS } from '../constants/origenProspecto';

const CerebroTareas = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [clients, setClients] = useState([]);
  const [prospectos, setProspectos] = useState([]);            // NUEVO: prospectos activos (db.prospectos)
  const [meetings, setMeetings] = useState([]);
  const [minutasJerarquicas, setMinutasJerarquicas] = useState([]);
  const [expandedMinutas, setExpandedMinutas] = useState({});
  // Selector unificado: value = "prospecto:{id}" o "cliente:{id}"
  const [selectedEntity, setSelectedEntity] = useState('');
  // Título por defecto con fecha y hora actual
  const getDefaultTitle = () => {
    const now = new Date();
    return `Reunión de fecha ${now.toLocaleDateString('es-CL', { day: '2-digit', month: '2-digit', year: 'numeric' })}, ${now.toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' })}`;
  };
  const [meetingTitle, setMeetingTitle] = useState(getDefaultTitle());
  const [transcription, setTranscription] = useState('');
  const [processing, setProcessing] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [areaFilter, setAreaFilter] = useState('all');
  const [showTransferidas, setShowTransferidas] = useState(false);
  
  // Estados para gestión documental
  const [showEditMinuta, setShowEditMinuta] = useState(false);
  const [selectedMinuta, setSelectedMinuta] = useState(null);
  const [editingMinuta, setEditingMinuta] = useState({ title: '', transcription: '', resumen_ia: '' });
  const [editingTareas, setEditingTareas] = useState([]);
  const [savingMinuta, setSavingMinuta] = useState(false);
  const [editTab, setEditTab] = useState('general');
  const [usuarios, setUsuarios] = useState([]);
  
  // Estado para panel de aprendizaje (solo Admin)
  const [showLearningPanel, setShowLearningPanel] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  
  useEffect(() => {
    // Obtener usuario actual del localStorage
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        setCurrentUser(user);
      } catch (e) {
        console.error('Error parsing user:', e);
      }
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [areaFilter, showTransferidas]);

  const fetchData = async () => {
    try {
      let params = [];
      if (areaFilter !== 'all') params.push(`area=${areaFilter}`);
      if (showTransferidas) params.push('incluir_transferidas=true');
      const queryString = params.length > 0 ? `?${params.join('&')}` : '';
      
      const { values, fallidos } = await settleRequests({
        'clients':                    api.get('/clients'),
        'prospectos':                 api.get('/marketing/prospectos'),
        'meetings':                   api.get('/meetings'),
        'tasks/minutas-jerarquicas':  api.get(`/tasks/minutas-jerarquicas${queryString}`),
      });
      if (values['clients'])                   setClients(values['clients'].data);
      if (values['prospectos']) {
        // Backend puede devolver array o {prospectos: [...]}
        const raw = values['prospectos'].data;
        const listaRaw = Array.isArray(raw) ? raw : (raw?.prospectos || []);
        // Solo prospectos aún NO cerrados (excluir Aceptado / Perdido / archivados)
        const ETAPAS_ABIERTAS = new Set(['Por Contactar', 'Contactado', 'Sin Respuesta', 'En Precalificación', 'Reunión Socios', 'Recuperado', 'Descartado']);
        const activos = listaRaw.filter(p => !p.archivado && ETAPAS_ABIERTAS.has(p.etapa));
        setProspectos(activos);
      }
      if (values['meetings'])                  setMeetings(values['meetings'].data);
      if (values['tasks/minutas-jerarquicas']) {
        const md = values['tasks/minutas-jerarquicas'].data;
        setMinutasJerarquicas(md);
        // Colapsar todas por defecto (acordeón cerrado)
        if (Object.keys(expandedMinutas).length === 0) {
          const collapsed = {};
          md.forEach(m => { collapsed[m.minuta_id] = false; });
          setExpandedMinutas(collapsed);
        }
      }
      if (fallidos.length) {
        console.error('[Cerebro] Endpoints con error:', fallidos);
        fallidos.forEach(f => toast.error(`Cerebro · ${f.detalle}`, { duration: 8000 }));
      }
    } catch (error) {
      console.error('[Cerebro] Error inesperado:', error);
      toast.error('Error inesperado en Cerebro de Tareas');
    }
  };

  const toggleMinutaExpanded = (minutaId) => {
    setExpandedMinutas(prev => ({
      ...prev,
      [minutaId]: !prev[minutaId]
    }));
  };

  // Función para subir archivo de transcripción
  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    // Validar extensión
    const allowedExtensions = ['.txt', '.docx', '.pdf', '.doc'];
    const ext = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
    if (!allowedExtensions.includes(ext)) {
      toast.error(`Tipo de archivo no soportado. Usa: ${allowedExtensions.join(', ')}`);
      return;
    }
    
    setUploadingFile(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await api.post('/transcription/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (response.data.success) {
        setTranscription(response.data.text);
        toast.success(`Archivo "${file.name}" cargado (${response.data.word_count} palabras)`);
      }
    } catch (error) {
      const errorMsg = error?.response?.data?.detail || 'Error al procesar el archivo';
      toast.error(errorMsg);
    } finally {
      setUploadingFile(false);
      // Limpiar el input para permitir subir el mismo archivo otra vez
      event.target.value = '';
    }
  };

  // Funciones de gestión documental
  const handleViewPDF = async (minutaId) => {
    try {
      const response = await api.post(`/meetings/${minutaId}/generate-pdf`, null, {
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (error) {
      toast.error(formatErrorMessage(error));
    }
  };

  const handleDownloadPDF = async (minutaId, title) => {
    try {
      const response = await api.post(`/meetings/${minutaId}/generate-pdf`, null, {
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `minuta_${title?.replace(/\s/g, '_') || minutaId.slice(0, 8)}.pdf`;
      link.click();
      toast.success('PDF descargado');
    } catch (error) {
      toast.error(formatErrorMessage(error));
    }
  };

  const handleOpenEditMinuta = async (minutaId) => {
    try {
      // Cargar minuta, tareas y usuarios en paralelo
      const [minutaRes, tareasRes, usuariosRes] = await Promise.all([
        api.get(`/meetings/${minutaId}`),
        api.get(`/tasks?meeting_id=${minutaId}`),
        api.get('/users/asignables')
      ]);
      
      setSelectedMinuta(minutaRes.data);
      setEditingMinuta({
        title: minutaRes.data.title || '',
        transcription: minutaRes.data.transcription || '',
        resumen_ia: minutaRes.data.ai_analysis?.resumen || ''
      });
      
      // Preparar tareas para edición con fecha de inicio
      const tareasConFechas = (tareasRes.data || []).map(t => ({
        ...t,
        fecha_inicio: t.created_at ? t.created_at.split('T')[0] : new Date().toISOString().split('T')[0],
        deadline: t.deadline ? (typeof t.deadline === 'string' ? t.deadline.split('T')[0] : new Date(t.deadline).toISOString().split('T')[0]) : ''
      }));
      setEditingTareas(tareasConFechas);
      
      // Combinar todos los usuarios disponibles para asignación
      const usuariosData = usuariosRes.data || {};
      const todosUsuarios = [
        ...(usuariosData.socios || []),
        ...(usuariosData.otros_lideres || []),
        ...(usuariosData.usuarios_area || [])
      ];
      setUsuarios(todosUsuarios);
      setEditTab('general');
      setShowEditMinuta(true);
    } catch (error) {
      toast.error(formatErrorMessage(error));
    }
  };

  const handleSaveMinuta = async () => {
    if (!selectedMinuta) return;
    setSavingMinuta(true);
    try {
      // Guardar cambios de la minuta (título y resumen IA)
      const minutaUpdate = {};
      if (editingMinuta.title !== selectedMinuta.title) minutaUpdate.title = editingMinuta.title;
      // NO enviamos transcription - es solo lectura
      if (editingMinuta.resumen_ia !== (selectedMinuta.ai_analysis?.resumen || '')) {
        minutaUpdate.resumen_ia = editingMinuta.resumen_ia;
      }
      
      if (Object.keys(minutaUpdate).length > 0) {
        await api.put(`/meetings/${selectedMinuta.id}`, minutaUpdate);
      }
      
      // Guardar cambios de cada tarea
      for (const tarea of editingTareas) {
        const tareaOriginal = (await api.get(`/tasks/${tarea.id}`)).data;
        
        // Construir objeto de actualización como body JSON
        const updateData = {};
        if (tarea.title !== tareaOriginal.title) updateData.title = tarea.title;
        if (tarea.description !== tareaOriginal.description) updateData.description = tarea.description;
        if (tarea.responsible !== tareaOriginal.responsible) updateData.responsible = tarea.responsible;
        if (tarea.area !== tareaOriginal.area) updateData.area = tarea.area;
        
        // Fechas
        const deadlineOriginal = tareaOriginal.deadline ? tareaOriginal.deadline.split('T')[0] : '';
        if (tarea.deadline !== deadlineOriginal) {
          updateData.deadline = tarea.deadline;
        }
        if (tarea.fecha_inicio) {
          updateData.fecha_inicio = tarea.fecha_inicio;
        }
        
        if (Object.keys(updateData).length > 0) {
          // Enviar como body JSON, no como query params
          await api.put(`/tasks/${tarea.id}/editar`, updateData);
        }
      }
      
      toast.success('Minuta y tareas actualizadas correctamente');
      setShowEditMinuta(false);
      fetchData();
    } catch (error) {
      toast.error(formatErrorMessage(error));
    } finally {
      setSavingMinuta(false);
    }
  };

  // Función para actualizar una tarea en el estado local
  const handleUpdateTareaLocal = (tareaId, field, value) => {
    setEditingTareas(prev => prev.map(t => 
      t.id === tareaId ? { ...t, [field]: value } : t
    ));
  };

  const handleDeleteMinuta = async (minutaId, title) => {
    if (!confirm(`¿Está seguro de eliminar la minuta "${title}"? Se eliminarán también las tareas asociadas.`)) return;
    
    try {
      await api.delete(`/meetings/${minutaId}`);
      toast.success('Minuta eliminada');
      setShowEditMinuta(false);
      setSelectedMinuta(null);
      fetchData();
    } catch (error) {
      toast.error(formatErrorMessage(error));
    }
  };

  const handleDeleteTask = async (taskId, title) => {
    if (!confirm(`¿Está seguro de eliminar la tarea "${title}"?`)) return;
    
    try {
      await api.delete(`/tasks/${taskId}`);
      toast.success('Tarea eliminada');
      fetchData();
    } catch (error) {
      toast.error(formatErrorMessage(error));
    }
  };

  const handleProcessTranscription = async () => {
    if (!selectedEntity || !meetingTitle || !transcription) {
      toast.error('Completa todos los campos');
      return;
    }

    // Parsear "prospecto:{id}" o "cliente:{id}"
    const [tipo, entityId] = selectedEntity.split(':');
    if (!entityId) {
      toast.error('Selección inválida');
      return;
    }

    setProcessing(true);
    try {
      const payload = {
        title: meetingTitle,
        transcription: transcription,
      };
      if (tipo === 'prospecto') payload.prospecto_id = entityId;
      else if (tipo === 'cliente') payload.client_id = entityId;
      else { toast.error('Tipo de entidad no reconocido'); return; }

      // Crear meeting
      const meetingRes = await api.post('/meetings', payload);

      // Procesar con IA
      await api.post(`/meetings/${meetingRes.data.id}/process`);

      toast.success('¡Transcripción procesada! Tareas creadas automáticamente');
      setTranscription('');
      setMeetingTitle('');
      setSelectedEntity('');
      fetchData();
    } catch (error) {
      toast.error(formatErrorMessage(error));
    } finally {
      setProcessing(false);
    }
  };

  const handleUpdateTaskStatus = async (taskId, newStatus) => {
    try {
      await api.put(`/tasks/${taskId}/status?new_status=${newStatus}`);
      toast.success('Estado actualizado');
      fetchData();
    } catch (error) {
      toast.error('Error al actualizar estado');
    }
  };

  // Renderizado de secciones individuales (para reordenamiento)
  return (
    <div className="min-h-screen flex" style={{ background: '#0B132B' }}>
      <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} />

      <div className="flex-1 p-8 overflow-y-auto max-h-screen relative">
        {/* Tech grid background */}
        <div 
          className="absolute inset-0 pointer-events-none opacity-30"
          style={{
            backgroundImage: `
              linear-gradient(rgba(58, 134, 255, 0.03) 1px, transparent 1px),
              linear-gradient(90deg, rgba(58, 134, 255, 0.03) 1px, transparent 1px)
            `,
            backgroundSize: '50px 50px'
          }}
        />
        
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="relative z-10"
        >
          {/* Header con elemento visual Brain holografico */}
          <div className="mb-8">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4 mb-2">
                {/* Brain holografico con glow */}
                <div className="relative">
                  <div 
                    className="absolute inset-0 blur-xl rounded-full"
                    style={{ background: 'radial-gradient(circle, rgba(0, 212, 255, 0.4) 0%, rgba(58, 134, 255, 0.2) 50%, transparent 70%)' }}
                  />
                  <div 
                    className="relative w-14 h-14 rounded-xl flex items-center justify-center"
                    style={{
                      background: 'linear-gradient(135deg, rgba(58, 134, 255, 0.2) 0%, rgba(0, 212, 255, 0.1) 100%)',
                      border: '1px solid rgba(0, 212, 255, 0.3)',
                      boxShadow: '0 0 30px rgba(0, 212, 255, 0.3), inset 0 0 20px rgba(58, 134, 255, 0.1)'
                    }}
                  >
                    <Brain 
                      className="w-8 h-8 text-[#00D4FF]" 
                      strokeWidth={1.5}
                      style={{ filter: 'drop-shadow(0 0 10px rgba(0, 212, 255, 0.6))' }}
                    />
                  </div>
                  {/* Neural network animation lines */}
                  <div className="absolute -inset-4 overflow-hidden opacity-50 pointer-events-none">
                    <div 
                      className="absolute top-1/2 left-0 right-0 h-px"
                      style={{
                        background: 'linear-gradient(90deg, transparent 0%, #00D4FF 50%, transparent 100%)',
                        animation: 'pulse 2s ease-in-out infinite'
                      }}
                    />
                  </div>
                </div>
                <div>
                  <h1 
                    className="text-4xl font-bold text-white" 
                    data-testid="cerebro-title"
                    style={{ textShadow: '0 0 30px rgba(0, 212, 255, 0.3)' }}
                  >
                    Cerebro de Tareas
                  </h1>
                  <p className="text-[#00D4FF]/80 text-sm tracking-wider uppercase">
                    Inteligencia Artificial
                  </p>
                </div>
              </div>
              {/* Boton de Aprendizaje - Solo Admin */}
              {(currentUser?.role === 'Admin' || currentUser?.role === 'Administrador') && (
                <Button
                  variant={showLearningPanel ? "default" : "outline"}
                  onClick={() => setShowLearningPanel(!showLearningPanel)}
                  className={`flex items-center gap-2 ${
                    showLearningPanel 
                      ? 'bg-gradient-to-r from-[#3A86FF] to-[#00D4FF] text-white border-none' 
                      : 'bg-transparent border-[#3A86FF]/30 text-[#00D4FF] hover:bg-[#3A86FF]/10'
                  }`}
                  data-testid="toggle-learning-panel-btn"
                >
                  <GraduationCap className="h-4 w-4" />
                  {showLearningPanel ? 'Ocultar Aprendizaje' : 'Panel de Aprendizaje'}
                </Button>
              )}
            </div>
            <p className="text-white/80 text-lg mt-2">
              Procesa transcripciones y extrae tareas automaticamente con IA
            </p>
          </div>
          
          {/* Panel de Aprendizaje del Cerebro (Solo Admin) */}
          {showLearningPanel && (currentUser?.role === 'Admin' || currentUser?.role === 'Administrador') && (
            <Card 
              className="p-6 mb-6 border-[#3A86FF]/20 backdrop-blur-sm"
              style={{ background: 'linear-gradient(135deg, rgba(58, 134, 255, 0.1) 0%, rgba(22, 32, 66, 0.8) 100%)' }}
            >
              <BrainLearningPanel />
            </Card>
          )}

          {/* Input Section - Transcripcion */}
          <Card 
            className="p-6 mb-6 border-[#3A86FF]/20 backdrop-blur-sm"
            style={{ background: 'rgba(22, 32, 66, 0.6)' }}
          >
            <div className="flex items-center gap-2 mb-4">
              <Sparkles className="w-5 h-5 text-[#00D4FF]" />
              <h2 className="text-xl font-semibold text-white">
                Procesar Nueva Transcripcion
              </h2>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-[11px] font-semibold text-white/70 uppercase tracking-wider mb-1.5">
                    Vincular Reunión a
                  </label>
                  <div className="relative">
                    <select
                      value={selectedEntity}
                      onChange={(e) => setSelectedEntity(e.target.value)}
                      data-testid="entity-select"
                      className="w-full pl-9 pr-4 py-2 bg-[#0B132B] border border-[#3A86FF]/20 rounded-lg text-sm text-white focus:ring-2 focus:ring-indigo-500 focus:bg-[#0B132B] transition-all outline-none appearance-none"
                    >
                      <option value="">-- Seleccionar Prospecto o Cliente --</option>
                      <optgroup label="Prospectos en Cierre (Módulo 2)">
                        {prospectos.map((p) => (
                          <option key={`p-${p.id}`} value={`prospecto:${p.id}`}>
                            [P] {p.nombre || p.email} — {p.empresa || 'Persona Natural'}
                          </option>
                        ))}
                        {prospectos.length === 0 && (
                          <option disabled>Sin prospectos activos</option>
                        )}
                      </optgroup>
                      <optgroup label="Master de Clientes">
                        {clients.map((c) => (
                          <option key={`c-${c.id}`} value={`cliente:${c.id}`}>
                            [C] {c.name} — {c.company || c.rut || 'Sin empresa'}
                          </option>
                        ))}
                        {clients.length === 0 && (
                          <option disabled>Sin clientes</option>
                        )}
                      </optgroup>
                    </select>
                    <div className="absolute left-3 top-2.5 text-slate-400 pointer-events-none">
                      <Search className="w-4 h-4" />
                    </div>
                  </div>
                  <p className="text-[10px] text-white/50 mt-1">
                    [P] Prospectos vienen de Marketing → Módulo Cierre · [C] Clientes vienen del Master
                  </p>
                  <NuevoProspectoDesdeCerebroButton
                    clients={clients}
                    onCreated={({ status, prospecto_id, client_id }) => {
                      // Refrescar listas y auto-seleccionar el resultado
                      fetchData();
                      if (status === "linked_to_client" && client_id) {
                        setSelectedEntity(`cliente:${client_id}`);
                      } else if (prospecto_id) {
                        setSelectedEntity(`prospecto:${prospecto_id}`);
                      }
                    }}
                  />
                </div>

                <div>
                  <Label htmlFor="meeting-title" className="text-white/90 mb-2 block">
                    Título de la Reunión
                  </Label>
                  <Input
                    id="meeting-title"
                    placeholder="Ej: Reunión inicial - Asesoría tributaria"
                    value={meetingTitle}
                    onChange={(e) => setMeetingTitle(e.target.value)}
                    data-testid="meeting-title-input"
                    className="bg-[#0B132B] text-white border-[#3A86FF]/30 placeholder:text-white/50"
                  />
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label htmlFor="transcription" className="text-white/90">
                    Transcripción de la Reunión
                  </Label>
                  <div className="flex items-center gap-2">
                    <input
                      type="file"
                      id="file-upload"
                      accept=".txt,.docx,.pdf,.doc"
                      onChange={handleFileUpload}
                      className="hidden"
                      data-testid="file-upload-input"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => document.getElementById('file-upload').click()}
                      disabled={uploadingFile}
                      className="text-white/80 border-[#3A86FF]/30 hover:bg-[#3A86FF]/10"
                    >
                      {uploadingFile ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                          Cargando...
                        </>
                      ) : (
                        <>
                          <Upload className="w-4 h-4 mr-1" />
                          Subir archivo
                        </>
                      )}
                    </Button>
                    <span className="text-xs text-white/60">.txt, .docx, .pdf</span>
                  </div>
                </div>
                <Textarea
                  id="transcription"
                  placeholder="Pega aquí la transcripción o sube un archivo (.txt, .docx, .pdf)..."
                  value={transcription}
                  onChange={(e) => setTranscription(e.target.value)}
                  rows={12}
                  className="font-mono text-sm bg-[#0B132B] text-white border-[#3A86FF]/30 placeholder:text-white/50"
                  data-testid="transcription-input"
                />
              </div>

              <Button
                onClick={handleProcessTranscription}
                disabled={processing}
                className="w-full bg-primary hover:bg-primary/90 py-4 text-base font-medium"
                data-testid="process-btn"
              >
                {processing ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Procesando con IA...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-5 h-5 mr-2" />
                    Procesar con IA
                  </>
                )}
              </Button>
            </div>
          </Card>

          {/* Tasks Section - Hierarchical View */}
          <div className="mb-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-semibold text-white">
                  Tareas por Minuta
                </h2>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Checkbox 
                    id="show-transferidas"
                    checked={showTransferidas}
                    onCheckedChange={setShowTransferidas}
                    data-testid="show-transferidas-checkbox"
                  />
                  <label htmlFor="show-transferidas" className="text-sm text-white/80 cursor-pointer">
                    Mostrar transferidas
                  </label>
                </div>
                <Select value={areaFilter} onValueChange={setAreaFilter}>
                  <SelectTrigger className="w-48 bg-[#0B132B] border-[#3A86FF]/20 text-white" data-testid="area-filter">
                    <SelectValue placeholder="Filtrar por área" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#162042] border-[#3A86FF]/20">
                    <SelectItem value="all" className="text-white hover:bg-[#3A86FF]/20">Todas las áreas</SelectItem>
                    <SelectItem value="Legal" className="text-white hover:bg-[#3A86FF]/20">Legal</SelectItem>
                    <SelectItem value="Tributaria" className="text-white hover:bg-[#3A86FF]/20">Tributaria</SelectItem>
                    <SelectItem value="Contabilidad" className="text-white hover:bg-[#3A86FF]/20">Contabilidad</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-4">
              {minutasJerarquicas.length === 0 ? (
                <Card className="p-8 text-center border-[#3A86FF]/20" style={{ background: 'rgba(22, 32, 66, 0.6)' }}>
                  <AlertCircle className="w-12 h-12 text-white/40 mx-auto mb-3" />
                  <p className="text-white/70">Aún no hay tareas. Procesa una transcripción para comenzar.</p>
                </Card>
              ) : (
                minutasJerarquicas.map((minuta) => (
                  <Card key={minuta.minuta_id} className={`border-[#3A86FF]/20 overflow-hidden ${minuta.transferido_operaciones ? 'opacity-70' : ''}`} style={{ background: 'rgba(22, 32, 66, 0.6)' }} data-testid={`minuta-${minuta.minuta_id}`}>
                    {/* Minuta Header - Parent */}
                    <div 
                      className={`p-5 cursor-pointer transition-colors ${minuta.transferido_operaciones ? 'bg-[#0B132B]/50' : 'hover:bg-[#3A86FF]/10'}`}
                      onClick={() => toggleMinutaExpanded(minuta.minuta_id)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          {expandedMinutas[minuta.minuta_id] ? (
                            <ChevronDown className="w-5 h-5 text-[#00D4FF]" />
                          ) : (
                            <ChevronRight className="w-5 h-5 text-white/60" />
                          )}
                          <div>
                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                              <span className="font-mono text-sm bg-[#3A86FF]/20 px-3 py-1 rounded text-[#3A86FF] font-semibold">
                                {minuta.folio_psp && minuta.folio_psp.startsWith('Minuta-') 
                                  ? minuta.folio_psp 
                                  : `Minuta N°${minuta.folio_psp}`}
                              </span>
                              <span className={`px-2 py-1 rounded text-xs font-medium ${
                                minuta.nomenclatura === 'Cliente/Proyecto' 
                                  ? 'bg-[#00E676]/20 text-[#00E676]' 
                                  : 'bg-[#FFB300]/20 text-[#FFB300]'
                              }`}>
                                {minuta.nomenclatura}
                              </span>
                              {minuta.client_origen && (
                                <span className="px-2 py-1 rounded text-xs font-medium bg-cyan-500/20 text-cyan-400">
                                  Origen: {minuta.client_origen}
                                </span>
                              )}
                              {minuta.transferido_operaciones && (
                                <span className="px-2 py-1 rounded text-xs font-medium bg-white/10 text-white flex items-center gap-1">
                                  <Archive className="w-3 h-3" />
                                  Transferido a Operaciones
                                </span>
                              )}
                            </div>
                            <h3 className="text-lg font-semibold text-white">
                              {minuta.prospecto_nombre}
                              {minuta.prospecto_empresa && (
                                <span className="text-white/70 font-normal text-base ml-2">
                                  ({minuta.prospecto_empresa})
                                </span>
                              )}
                            </h3>
                            <p className="text-sm text-white/90 mt-1">{minuta.titulo_minuta}</p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-4">
                          {/* Progress Summary */}
                          <div className="text-right">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-sm font-medium text-white/90">
                                {minuta.tareas_completadas} de {minuta.tareas_totales} completadas
                              </span>
                            </div>
                            <Progress value={minuta.progreso_porcentaje} className="w-32 h-2" />
                            <span className="text-xs text-white/70">{minuta.progreso_porcentaje}%</span>
                          </div>
                          
                          {/* Areas Tags */}
                          <div className="flex flex-wrap gap-1">
                            {minuta.areas_involucradas.map(area => (
                              <span key={area} className="px-2 py-1 rounded-full text-xs bg-[#3A86FF]/20 text-[#00D4FF]">
                                {area}
                              </span>
                            ))}
                          </div>
                          
                          {/* Document Management Buttons */}
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-white/80 hover:text-white hover:bg-white/10"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleViewPDF(minuta.minuta_id);
                              }}
                              title="Visualizar PDF"
                              data-testid={`view-pdf-${minuta.minuta_id}`}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            {!minuta.transferido_operaciones && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-white/80 hover:text-white hover:bg-white/10"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleOpenEditMinuta(minuta.minuta_id);
                                }}
                                title="Editar Minuta"
                                data-testid={`edit-minuta-${minuta.minuta_id}`}
                              >
                                <Edit2 className="w-4 h-4" />
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-white/80 hover:text-white hover:bg-white/10"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDownloadPDF(minuta.minuta_id, minuta.titulo_minuta);
                              }}
                              title="Descargar PDF"
                              data-testid={`download-pdf-${minuta.minuta_id}`}
                            >
                              <Download className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-400 hover:text-red-300 hover:bg-red-500/20"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteMinuta(minuta.minuta_id, minuta.titulo_minuta);
                              }}
                              title="Eliminar Minuta"
                              data-testid={`delete-minuta-${minuta.minuta_id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                          
                          {/* Link to PSP */}
                          {minuta.psp_id && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="border-[#3A86FF]/50 text-[#3A86FF] hover:bg-[#3A86FF]/20 hover:text-white"
                              onClick={(e) => {
                                e.stopPropagation();
                                window.location.href = `/propuestas?psp=${minuta.psp_id}`;
                              }}
                              data-testid={`link-psp-${minuta.psp_id}`}
                            >
                              <ExternalLink className="w-4 h-4 mr-1" />
                              Ver PSP
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Child Tasks */}
                    <AnimatePresence>
                      {expandedMinutas[minuta.minuta_id] && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.2 }}
                        >
                          <div className="border-t border-[#3A86FF]/20">
                            {/* Sección de Credenciales Jerárquicas del Cliente */}
                            {minuta.client_id && (
                              <div className="p-4 border-b border-[#3A86FF]/20 bg-gradient-to-r from-[#FFB300]/10 to-transparent">
                                <HierarchicalCredentials
                                  clientName={minuta.prospecto_nombre || minuta.client_name}
                                  clientRut={minuta.client_rut}
                                  clientClaveSii={minuta.client_clave_sii}
                                  clientClaveUnica={minuta.client_clave_unica}
                                  empresas={minuta.client_empresas || []}
                                />
                              </div>
                            )}
                            
                            {minuta.tareas.length === 0 ? (
                              <div className="p-4 text-center text-white/70">
                                No hay tareas asociadas a esta minuta
                              </div>
                            ) : (
                              minuta.tareas.map((task, index) => (
                                <div 
                                  key={task.id} 
                                  className={`p-4 pl-12 ${index !== minuta.tareas.length - 1 ? 'border-b border-[#3A86FF]/10' : ''}`}
                                  data-testid={`task-${task.id}`}
                                >
                                  <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                      <div className="flex items-center gap-3 mb-2">
                                        <span className="px-3 py-1 rounded-full text-xs font-medium bg-[#3A86FF]/20 text-[#3A86FF]">
                                          {task.area}
                                        </span>
                                        <span
                                          className={`px-3 py-1 rounded-full text-xs font-medium ${
                                            task.status === 'Completada'
                                              ? 'bg-[#00E676]/20 text-[#00E676]'
                                              : task.status === 'En Progreso'
                                              ? 'bg-[#3A86FF]/20 text-[#3A86FF]'
                                              : task.status === 'Pendiente de Asignar'
                                              ? 'bg-purple-500/20 text-purple-400'
                                              : 'bg-[#FFB300]/20 text-[#FFB300]'
                                          }`}
                                        >
                                          {task.status}
                                        </span>
                                        {task.attachments?.length > 0 && (
                                          <span className="flex items-center gap-1 text-xs text-white/70">
                                            <Paperclip className="w-3 h-3" />
                                            {task.attachments.length}
                                          </span>
                                        )}
                                      </div>
                                      <h4 className="text-base font-semibold text-white mb-1">{task.title}</h4>
                                      <p className="text-sm text-white/80 mb-2">{task.description}</p>
                                      <div className="flex items-center gap-4 text-sm text-white/70">
                                        <span className="flex items-center gap-1">
                                          <Users className="w-4 h-4" />
                                          {task.responsible || 'Sin asignar'}
                                        </span>
                                        <span>
                                          Vence: {new Date(task.deadline).toLocaleDateString('es-CL')}
                                        </span>
                                      </div>
                                    </div>
                                    <Select
                                      value={task.status}
                                      onValueChange={(value) => handleUpdateTaskStatus(task.id, value)}
                                    >
                                      <SelectTrigger className="w-44 bg-[#0B132B] border-[#3A86FF]/20 text-white" data-testid={`task-status-select-${task.id}`}>
                                        <SelectValue />
                                      </SelectTrigger>
                                      <SelectContent className="bg-[#162042] border-[#3A86FF]/20">
                                        <SelectItem value="Pendiente de Asignar" className="text-white hover:bg-[#3A86FF]/20">Pendiente de Asignar</SelectItem>
                                        <SelectItem value="Pendiente" className="text-white hover:bg-[#3A86FF]/20">Pendiente</SelectItem>
                                        <SelectItem value="En Progreso" className="text-white hover:bg-[#3A86FF]/20">En Progreso</SelectItem>
                                        <SelectItem value="Completada" className="text-white hover:bg-[#3A86FF]/20">Completada</SelectItem>
                                      </SelectContent>
                                    </Select>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="text-[#FF5252] hover:text-[#FF5252] hover:bg-[#FF5252]/10 ml-2"
                                      onClick={() => handleDeleteTask(task.id, task.title)}
                                      title="Eliminar Tarea"
                                      data-testid={`delete-task-${task.id}`}
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  </div>
                                </div>
                              ))
                            )}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </Card>
                ))
              )}
            </div>
          </div>

          {/* Recent Meetings */}
          {meetings.length > 0 && (
            <div>
              <h2 className="text-2xl font-semibold text-white mb-4">
                Reuniones Recientes
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {meetings.slice(0, 4).map((meeting) => (
                  <Card key={meeting.id} className="p-5 border-[#3A86FF]/20" style={{ background: 'rgba(22, 32, 66, 0.6)' }} data-testid={`meeting-${meeting.id}`}>
                    <div className="flex items-start justify-between mb-3">
                      <FileText className="w-5 h-5 text-[#3A86FF]" strokeWidth={1.5} />
                      <div className="flex items-center gap-2">
                        {meeting.processed && (
                          <span className="px-2 py-1 bg-[#00E676]/20 text-[#00E676] text-xs rounded-full font-medium">
                            Procesada
                          </span>
                        )}
                        <button
                          onClick={() => handleDeleteMinuta(meeting.id, meeting.title)}
                          className="p-1 text-[#FF5252] hover:text-[#FF5252] hover:bg-[#FF5252]/10 rounded"
                          title="Eliminar reunión"
                          data-testid={`delete-meeting-${meeting.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    <h3 className="font-semibold text-white mb-1">{meeting.title}</h3>
                    <p className="text-sm text-white/70">
                      {new Date(meeting.created_at).toLocaleDateString('es-CL')}
                    </p>
                    {meeting.ai_analysis && (
                      <p className="text-sm text-white/80 mt-2 line-clamp-2">
                        {meeting.ai_analysis.resumen}
                      </p>
                    )}
                  </Card>
                ))}
              </div>
            </div>
          )}
        </motion.div>
      </div>

      {/* Modal para editar Minuta */}
      <Dialog open={showEditMinuta} onOpenChange={setShowEditMinuta}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-[#162042] border-[#3A86FF]/20">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-white">
              <Edit2 className="w-5 h-5" />
              Editar Minuta
            </DialogTitle>
            <DialogDescription className="text-white/70">
              Edite el título, resumen IA y tareas. La transcripción es de solo lectura.
            </DialogDescription>
          </DialogHeader>
          {selectedMinuta && (
            <div className="mt-4">
              {/* Tabs de navegación */}
              <Tabs value={editTab} onValueChange={setEditTab} className="w-full">
                <TabsList className="grid w-full grid-cols-3 mb-4 bg-[#0B132B] border border-[#3A86FF]/20">
                  <TabsTrigger value="general" className="flex items-center gap-2 text-white/70 data-[state=active]:bg-[#3A86FF]/20 data-[state=active]:text-white" data-testid="edit-tab-general">
                    <FileText className="w-4 h-4" />
                    Datos Generales
                  </TabsTrigger>
                  <TabsTrigger value="resumen" className="flex items-center gap-2 text-white/70 data-[state=active]:bg-[#3A86FF]/20 data-[state=active]:text-white" data-testid="edit-tab-resumen">
                    <Sparkles className="w-4 h-4" />
                    Resumen IA
                  </TabsTrigger>
                  <TabsTrigger value="tareas" className="flex items-center gap-2 text-white/70 data-[state=active]:bg-[#3A86FF]/20 data-[state=active]:text-white" data-testid="edit-tab-tareas">
                    <ListChecks className="w-4 h-4" />
                    Tareas ({editingTareas.length})
                  </TabsTrigger>
                </TabsList>
                
                {/* Tab: Datos Generales */}
                <TabsContent value="general" className="space-y-4">
                  <div>
                    <Label className="text-white/90">Título de la Reunión</Label>
                    <Input
                      value={editingMinuta.title}
                      onChange={(e) => setEditingMinuta({ ...editingMinuta, title: e.target.value })}
                      className="bg-[#0B132B] text-white border-[#3A86FF]/30"
                      data-testid="edit-minuta-title"
                    />
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <Label className="flex items-center gap-2">
                        <Lock className="w-3 h-3 text-white/60" />
                        Transcripción (Solo Lectura)
                      </Label>
                    </div>
                    <div className="p-4 bg-[#162042] rounded-lg border border-[#3A86FF]/20 max-h-[300px] overflow-y-auto">
                      <p className="text-sm text-white/80 whitespace-pre-wrap font-mono">
                        {editingMinuta.transcription || 'Sin transcripción'}
                      </p>
                    </div>
                  </div>
                </TabsContent>
                
                {/* Tab: Resumen IA */}
                <TabsContent value="resumen" className="space-y-4">
                  <div>
                    <Label className="flex items-center gap-2 mb-2 text-white">
                      <Sparkles className="w-4 h-4 text-purple-500" />
                      Resumen IA (Editable)
                    </Label>
                    <Textarea
                      value={editingMinuta.resumen_ia}
                      onChange={(e) => setEditingMinuta({ ...editingMinuta, resumen_ia: e.target.value })}
                      rows={10}
                      className="text-sm bg-[#0B132B] text-white border-[#3A86FF]/30 placeholder:text-white/50"
                      placeholder="Ingrese o modifique el resumen generado por IA..."
                      data-testid="edit-minuta-resumen"
                    />
                    <p className="text-xs text-white/70 mt-1">
                      Este resumen se incluirá en el PDF de la minuta.
                    </p>
                  </div>
                </TabsContent>
                
                {/* Tab: Tareas */}
                <TabsContent value="tareas" className="space-y-4">
                  <div className="bg-amber-500/10 p-3 rounded-lg border border-amber-500/20 mb-4">
                    <p className="text-xs text-amber-400 flex items-center gap-2">
                      <AlertCircle className="w-4 h-4" />
                      Los plazos son preliminares y se reflejarán en la Carta Gantt del PDF.
                    </p>
                  </div>
                  
                  {editingTareas.length === 0 ? (
                    <div className="text-center py-8 text-white/70">
                      <ListChecks className="w-12 h-12 mx-auto mb-2 opacity-30" />
                      <p>No hay tareas registradas en esta minuta</p>
                    </div>
                  ) : (
                    <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2">
                      {editingTareas.map((tarea, idx) => (
                        <Card key={tarea.id} className="p-4 border-l-4 border-l-blue-500" data-testid={`edit-tarea-${idx}`}>
                          <div className="space-y-3">
                            <div className="flex items-center gap-2 mb-2">
                              <Badge variant="outline" className="text-xs">{idx + 1}</Badge>
                              <Badge className="text-xs bg-blue-100 text-blue-700">{tarea.area}</Badge>
                            </div>
                            
                            {/* Título */}
                            <div>
                              <Label className="text-xs text-white/70">Título</Label>
                              <Input
                                value={tarea.title || ''}
                                onChange={(e) => handleUpdateTareaLocal(tarea.id, 'title', e.target.value)}
                                className="text-sm"
                                data-testid={`edit-tarea-title-${idx}`}
                              />
                            </div>
                            
                            {/* Descripción */}
                            <div>
                              <Label className="text-xs text-white/70">Descripción</Label>
                              <Textarea
                                value={tarea.description || ''}
                                onChange={(e) => handleUpdateTareaLocal(tarea.id, 'description', e.target.value)}
                                rows={2}
                                className="text-sm"
                                data-testid={`edit-tarea-desc-${idx}`}
                              />
                            </div>
                            
                            {/* Fila: Responsable + Área */}
                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <Label className="text-xs text-white">Responsable</Label>
                                <Select
                                  value={tarea.responsible || ''}
                                  onValueChange={(val) => handleUpdateTareaLocal(tarea.id, 'responsible', val)}
                                >
                                  <SelectTrigger className="text-sm bg-white text-slate-800" data-testid={`edit-tarea-responsible-${idx}`}>
                                    <SelectValue placeholder="Seleccionar..." />
                                  </SelectTrigger>
                                  <SelectContent className="bg-white">
                                    {usuarios.map(u => (
                                      <SelectItem key={u.id} value={u.name}>{u.name}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                              <div>
                                <Label className="text-xs text-white">Área</Label>
                                <Select
                                  value={tarea.area || ''}
                                  onValueChange={(val) => handleUpdateTareaLocal(tarea.id, 'area', val)}
                                >
                                  <SelectTrigger className="text-sm bg-white text-slate-800" data-testid={`edit-tarea-area-${idx}`}>
                                    <SelectValue placeholder="Seleccionar..." />
                                  </SelectTrigger>
                                  <SelectContent className="bg-white">
                                    <SelectItem value="Tributaria">Tributaria</SelectItem>
                                    <SelectItem value="Contabilidad">Contabilidad</SelectItem>
                                    <SelectItem value="Legal">Legal</SelectItem>
                                    <SelectItem value="Comercial">Comercial</SelectItem>
                                    <SelectItem value="Adm. y Finanzas">Adm. y Finanzas</SelectItem>
                                    <SelectItem value="Socios">Socios</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                            </div>
                            
                            {/* Fila: Fechas */}
                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <Label className="text-xs text-slate-300 flex items-center gap-1">
                                  <Calendar className="w-3 h-3" />
                                  Fecha Inicio
                                </Label>
                                <Input
                                  type="date"
                                  value={tarea.fecha_inicio || ''}
                                  onChange={(e) => handleUpdateTareaLocal(tarea.id, 'fecha_inicio', e.target.value)}
                                  className="text-sm bg-white text-slate-800"
                                  data-testid={`edit-tarea-inicio-${idx}`}
                                />
                              </div>
                              <div>
                                <Label className="text-xs text-slate-300 flex items-center gap-1">
                                  <Calendar className="w-3 h-3" />
                                  Fecha Fin (Plazo)
                                </Label>
                                <Input
                                  type="date"
                                  value={tarea.deadline || ''}
                                  onChange={(e) => handleUpdateTareaLocal(tarea.id, 'deadline', e.target.value)}
                                  className="text-sm bg-white text-slate-800"
                                  data-testid={`edit-tarea-deadline-${idx}`}
                                />
                              </div>
                            </div>
                          </div>
                        </Card>
                      ))}
                    </div>
                  )}
                </TabsContent>
              </Tabs>
              
              {/* Botones de acción */}
              <div className="flex gap-3 pt-6 border-t mt-6">
                <Button 
                  onClick={handleSaveMinuta} 
                  disabled={savingMinuta}
                  className="flex-1"
                  data-testid="save-minuta-btn"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {savingMinuta ? 'Guardando...' : 'Guardar Todos los Cambios'}
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => handleViewPDF(selectedMinuta.id)}
                  data-testid="preview-minuta-pdf-btn"
                >
                  <Eye className="w-4 h-4 mr-2" />
                  Ver PDF con Gantt
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CerebroTareas;


// ==================== NUEVO PROSPECTO DESDE CEREBRO ====================
// Modal minimalista que llama a POST /api/cerebro/prospectos/match-or-create.
// Deja que el backend decida si crea, reutiliza prospecto o vincula a cliente.

const NuevoProspectoDesdeCerebroButton = ({ clients, onCreated }) => {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  // Idempotency key: se genera 1 vez al abrir el modal, se preserva ante reintentos
  // hasta que la operación tenga éxito (evita duplicados por doble click / red inestable).
  const [idemKey, setIdemKey] = useState(null);
  // Estado de conflicto 409: guarda code + candidatos para pedir selección manual.
  const [conflict, setConflict] = useState(null);
  const [chosenId, setChosenId] = useState('');
  const [chosenType, setChosenType] = useState(''); // "client" | "prospecto"
  // Estado de "vinculación pendiente": permite reintentar sin recrear.
  const [pendingLink, setPendingLink] = useState(null);
  const [form, setForm] = useState({
    nombre: '',
    email: '',
    telefono: '',
    empresa: '',
    rut: '',
    origen: '',              // OBLIGATORIO — sin preselección (usuario debe elegir)
    referido_por_client_id: '',
    mensaje: '',
  });

  const openModal = () => {
    // Generar key nueva por sesión de modal
    const uid = (typeof crypto !== 'undefined' && crypto.randomUUID)
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    setIdemKey(`cerebro:modal:${uid}`);
    setForm({ nombre: '', email: '', telefono: '', empresa: '', rut: '', origen: '', referido_por_client_id: '', mensaje: '' });
    setConflict(null);
    setChosenId('');
    setChosenType('');
    setPendingLink(null);
    setOpen(true);
  };

  const closeAndReset = () => {
    setOpen(false);
    setIdemKey(null);
    setConflict(null);
    setChosenId('');
    setChosenType('');
    setPendingLink(null);
  };

  // Ejecuta el POST match-or-create con posibles flags de desambiguación.
  // `extra` puede contener force_client_id o force_prospecto_id.
  const submit = async (extra = {}) => {
    setSaving(true);
    try {
      const payload = {
        nombre: form.nombre.trim(),
        email: form.email.trim() || null,
        telefono: form.telefono.trim() || null,
        empresa: form.empresa.trim() || null,
        rut: form.rut.trim() || null,
        mensaje: form.mensaje.trim() || null,
        // Origen elegido explícitamente por el usuario en el modal. El backend
        // lo valida contra el enum OrigenProspecto (mismo catálogo que Ventas).
        origen: form.origen,
        referido_por_client_id: form.referido_por_client_id || null,
        idempotency_key: idemKey,
        ...extra,
      };
      const res = await api.post('/cerebro/prospectos/match-or-create', payload);
      const data = res.data || {};
      // ¿Vinculación pendiente? Mantener modal abierto y ofrecer reintento.
      if (data.meeting_link_pending) {
        setPendingLink(data);
        toast.warning('Prospecto creado pero la reunión NO se pudo vincular. Puedes reintentar.');
        onCreated?.(data);
        setSaving(false);
        return;
      }
      if (data.status === 'linked_to_client') {
        toast.success(`Reunión vinculada al cliente "${data.client_name}".`);
      } else if (data.status === 'reused_prospecto') {
        toast.success(`Prospecto existente reutilizado: "${data.prospecto_nombre}".`);
      } else {
        toast.success('Prospecto creado desde Cerebro.');
      }
      onCreated?.(data);
      closeAndReset();
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      // 409 con lista de candidatos → pedir selección manual.
      if (status === 409 && detail && typeof detail === 'object' && detail.code) {
        setConflict(detail);
        setChosenId('');
        setChosenType(
          (detail.clientes || detail.candidatos)?.[0]?.name !== undefined ? 'client' : 'prospecto'
        );
        if (detail.code === 'simultaneous_client_and_prospecto_match') {
          toast.warning('Coincidencias en cliente Y prospecto. Selecciona la entidad correcta.');
        } else if (detail.code === 'meeting_already_linked_to_other_entity') {
          toast.error('La reunión ya está vinculada a otra entidad.');
        } else if (detail.code === 'idempotency_key_payload_mismatch') {
          toast.error('El request ya se procesó con datos distintos. Reabre el modal para reintentar.');
        } else {
          toast.warning('Múltiples coincidencias. Selecciona la entidad correcta.');
        }
      } else {
        // No cerramos el modal: preservamos idemKey para permitir reintento sin duplicar.
        toast.error(formatErrorMessage(e));
      }
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    if ((form.nombre || '').trim().length < 2) {
      toast.error('Ingresa el nombre del prospecto (mín. 2 caracteres).');
      return;
    }
    if (!form.origen) {
      toast.error('Selecciona el Origen del prospecto.');
      return;
    }
    await submit();
  };

  // Envío tras la selección manual de un candidato en un 409.
  const handleResolveConflict = async () => {
    if (!chosenId) {
      toast.error('Selecciona una entidad para continuar.');
      return;
    }
    const extra = chosenType === 'client'
      ? { force_client_id: chosenId }
      : { force_prospecto_id: chosenId };
    setConflict(null);
    await submit(extra);
  };

  // Reintentar la vinculación pendiente con la misma idempotency_key.
  const handleRetryLink = async () => {
    setPendingLink(null);
    await submit();
  };

  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="mt-2 h-8 px-2.5 text-xs bg-white/5 border-white/20 text-white hover:bg-white/10"
        onClick={openModal}
        data-testid="cerebro-new-prospecto-btn"
      >
        <Plus className="w-3.5 h-3.5 mr-1" /> Nuevo prospecto
      </Button>
      <Dialog open={open} onOpenChange={(o) => (o ? setOpen(o) : closeAndReset())}>
        <DialogContent className="max-w-lg" data-testid="cerebro-new-prospecto-dialog">
          <DialogHeader>
            <DialogTitle>Nuevo prospecto desde Cerebro</DialogTitle>
            <DialogDescription>
              Si los datos coinciden con un cliente o prospecto existente, se reutiliza y se vincula la reunión.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Input
              placeholder="Nombre *"
              value={form.nombre}
              onChange={(e) => setForm({ ...form, nombre: e.target.value })}
              data-testid="cerebro-new-prospecto-nombre"
            />
            <div className="grid grid-cols-2 gap-2">
              <Input
                placeholder="Email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                data-testid="cerebro-new-prospecto-email"
              />
              <Input
                placeholder="Teléfono"
                value={form.telefono}
                onChange={(e) => setForm({ ...form, telefono: e.target.value })}
                data-testid="cerebro-new-prospecto-tel"
              />
            </div>
            <Input
              placeholder="Empresa"
              value={form.empresa}
              onChange={(e) => setForm({ ...form, empresa: e.target.value })}
              data-testid="cerebro-new-prospecto-empresa"
            />
            <Input
              placeholder="RUT (opcional, ej: 12.345.678-9)"
              value={form.rut}
              onChange={(e) => setForm({ ...form, rut: e.target.value })}
              data-testid="cerebro-new-prospecto-rut"
            />
            <div>
              <Label className="text-xs text-slate-600 mb-1 block">
                Origen <span className="text-rose-600">*</span>
              </Label>
              <select
                className="w-full border rounded-md px-3 py-2 text-sm bg-white"
                value={form.origen}
                onChange={(e) => setForm({ ...form, origen: e.target.value })}
                data-testid="cerebro-new-prospecto-origen"
                required
              >
                <option value="" disabled>-- Selecciona un origen --</option>
                {ORIGEN_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
              <p className="text-[11px] text-slate-500 mt-1">
                Mismo catálogo que Ventas. El origen queda registrado como antecedente comercial del prospecto y de esta reunión.
              </p>
            </div>
            <div>
              <Label className="text-xs text-slate-600 mb-1 block">
                Cliente referente (opcional)
              </Label>
              <select
                className="w-full border rounded-md px-3 py-2 text-sm bg-white"
                value={form.referido_por_client_id}
                onChange={(e) => setForm({ ...form, referido_por_client_id: e.target.value })}
                data-testid="cerebro-new-prospecto-referente"
              >
                <option value="">-- Sin referente --</option>
                {(clients || []).map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}{c.company ? ` · ${c.company}` : ''}
                  </option>
                ))}
              </select>
            </div>
            <Textarea
              rows={3}
              placeholder="Contexto o mensaje capturado en la reunión"
              value={form.mensaje}
              onChange={(e) => setForm({ ...form, mensaje: e.target.value })}
              data-testid="cerebro-new-prospecto-mensaje"
            />

            {/* Bloque: selección manual ante 409 con candidatos */}
            {conflict && (conflict.candidatos || conflict.clientes || conflict.prospectos) && (
              <div
                className="rounded-md border border-amber-300 bg-amber-50 p-3 space-y-2"
                data-testid="cerebro-conflict-block"
              >
                <div className="text-sm font-medium text-amber-900">
                  {conflict.code === 'simultaneous_client_and_prospecto_match'
                    ? 'Los datos coinciden con un cliente y un prospecto distinto. Elige a cuál vincular:'
                    : 'Múltiples coincidencias. Elige una entidad para continuar:'}
                </div>
                <div className="space-y-1 max-h-56 overflow-y-auto">
                  {(conflict.clientes || (conflict.candidatos && conflict.code === 'multiple_client_matches' ? conflict.candidatos : []) || []).map((c) => (
                    <label
                      key={`cli-${c.id}`}
                      className="flex items-start gap-2 text-xs bg-white border rounded p-2 cursor-pointer hover:bg-amber-100"
                      data-testid={`cerebro-conflict-client-${c.id}`}
                    >
                      <input
                        type="radio"
                        name="conflict-choice"
                        value={c.id}
                        checked={chosenType === 'client' && chosenId === c.id}
                        onChange={() => { setChosenType('client'); setChosenId(c.id); }}
                      />
                      <span>
                        <span className="font-semibold">Cliente</span> · {c.name}
                        {c.email ? ` · ${c.email}` : ''}{c.rut ? ` · ${c.rut}` : ''}
                      </span>
                    </label>
                  ))}
                  {(conflict.prospectos || (conflict.candidatos && conflict.code === 'multiple_prospecto_matches' ? conflict.candidatos : []) || []).map((p) => (
                    <label
                      key={`pro-${p.id}`}
                      className="flex items-start gap-2 text-xs bg-white border rounded p-2 cursor-pointer hover:bg-amber-100"
                      data-testid={`cerebro-conflict-prospecto-${p.id}`}
                    >
                      <input
                        type="radio"
                        name="conflict-choice"
                        value={p.id}
                        checked={chosenType === 'prospecto' && chosenId === p.id}
                        onChange={() => { setChosenType('prospecto'); setChosenId(p.id); }}
                      />
                      <span>
                        <span className="font-semibold">Prospecto</span> · {p.nombre}
                        {p.email ? ` · ${p.email}` : ''}{p.rut ? ` · ${p.rut}` : ''}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Bloque: reintento tras vinculación pendiente */}
            {pendingLink && (
              <div
                className="rounded-md border border-blue-300 bg-blue-50 p-3 text-xs text-blue-900"
                data-testid="cerebro-pending-link-block"
              >
                El prospecto se guardó pero la vinculación de la reunión quedó pendiente.
                Presiona <b>Reintentar vinculación</b> para asociarla ahora (misma idempotency_key,
                sin duplicar).
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={closeAndReset} disabled={saving}>
              Cerrar
            </Button>
            {conflict ? (
              <Button
                size="sm"
                onClick={handleResolveConflict}
                disabled={saving || !chosenId}
                data-testid="cerebro-conflict-confirm"
              >
                {saving && <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />}
                Confirmar selección
              </Button>
            ) : pendingLink ? (
              <Button
                size="sm"
                onClick={handleRetryLink}
                disabled={saving}
                data-testid="cerebro-retry-link"
              >
                {saving && <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />}
                Reintentar vinculación
              </Button>
            ) : (
              <Button
                size="sm"
                onClick={handleSave}
                disabled={saving}
                data-testid="cerebro-new-prospecto-save"
              >
                {saving && <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />}
                Crear y vincular
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

